import type { DraftOperation } from './draftOperation.logic.ts'

export type PendingLocalOperation = {
  id: string
  code: string
  draftId: number
  expectedRevision: number | null
  racksJson: Record<string, unknown>
  cableTraysJson: Record<string, unknown>
  operation: DraftOperation
  createdAt: number
}

export type RecoveryStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

function storageKey(code: string, draftId: number): string {
  return `dc-cable-link:pending-operation:${code}:${draftId}`
}

export function readPendingLocalOperation(
  storage: RecoveryStorage | undefined,
  code: string,
  draftId: number,
): PendingLocalOperation | null {
  if (!storage) return null
  try {
    const raw = storage.getItem(storageKey(code, draftId))
    if (!raw) return null
    const value = JSON.parse(raw) as PendingLocalOperation
    return value?.id && value.code === code && value.draftId === draftId ? value : null
  } catch {
    return null
  }
}

export function writePendingLocalOperation(
  storage: RecoveryStorage | undefined,
  operation: PendingLocalOperation,
): void {
  if (!storage) return
  try {
    storage.setItem(storageKey(operation.code, operation.draftId), JSON.stringify(operation))
  } catch {
    // Local recovery must never turn a normal server save into a failed save.
    return
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('dc-pending-operation-changed'))
}

export function clearPendingLocalOperation(
  storage: RecoveryStorage | undefined,
  code: string,
  draftId: number,
  operationId?: string,
): void {
  if (!storage) return
  const key = storageKey(code, draftId)
  if (operationId) {
    const current = readPendingLocalOperation(storage, code, draftId)
    if (current?.id !== operationId) return
  }
  try {
    storage.removeItem(key)
  } catch {
    return
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('dc-pending-operation-changed'))
}

export function previewPendingLocalOperation(
  pending: PendingLocalOperation | null,
  currentRevision: number | null,
): { status: 'none' | 'replayable' | 'conflict'; expectedRevision: number | null; currentRevision: number | null } {
  if (!pending) return { status: 'none', expectedRevision: null, currentRevision }
  const status = pending.expectedRevision === currentRevision ? 'replayable' : 'conflict'
  return { status, expectedRevision: pending.expectedRevision, currentRevision }
}
